import { type ApolloError, makeVar, useReactiveVar } from '@apollo/client';
import deepEqual from 'react-fast-compare';
import type RealTimeBiddingDetails from '../../../types/realTimeBidding/RealTimeBiddingDetails';

export type RealTimeBiddingByOfferId = Record<string, RealTimeBiddingDetails>;

export interface RealTimeBidding {
  data: RealTimeBiddingByOfferId | undefined;
  loading: boolean;
  error: ApolloError | undefined;
}

const initialState = {
  data: undefined,
  loading: true,
  error: undefined,
};

const realTimeBidding = makeVar<RealTimeBidding>(initialState);

export const setRealTimeBidding = (
  realTimeBiddingValue: RealTimeBiddingByOfferId | undefined,
  more?: Partial<RealTimeBidding>,
) => {
  const current = realTimeBidding();
  if (!deepEqual(current.data, realTimeBiddingValue) || more) {
    realTimeBidding({
      ...current,
      data: realTimeBiddingValue,
      ...more,
    });
  }
};

/**
 * Should be used only for tests
 */
export const UNSAFE_getRealTimeBidding = () => realTimeBidding();

/**
 * Helper method to determine if real time bidding should display
 * The condition are:
 * 1. Real time bidding is active (we are on the modify page and current offer is tied to an auction)
 * 2. pax across bids > seats
 * 3. There is available seats (more than 0)
 */
const isShowRealTimeBidding = (realTimeBidding?: RealTimeBiddingDetails) =>
  Boolean(realTimeBidding) && realTimeBidding?.auctionOpen;

const useRealTimeBidding = <T = RealTimeBidding>() => {
  const reactiveRealTimeBidding = useReactiveVar(realTimeBidding);

  /**
   * Helper method to get the real time bidding details for the current card (by orderId)
   */
  const getRealTimeBiddingByOrderId = (orderId: string) => realTimeBidding()?.data?.[orderId];

  return {
    actions: {
      setRealTimeBidding,
      resetRealTimeBidding: () => realTimeBidding(initialState),
      setLoading: (loading: boolean) => realTimeBidding({ ...realTimeBidding(), loading }),
      setError: (error: ApolloError | undefined) => realTimeBidding({ ...realTimeBidding(), error }),
    },
    selectors: {
      getRealTimeBiddingByOrderId: (orderId: string) => getRealTimeBiddingByOrderId(orderId),
      isShowRealTimeBidding: (realTimeBidding: RealTimeBiddingDetails | undefined) =>
        isShowRealTimeBidding(realTimeBidding),
    },
    states: {
      realTimeBidding: reactiveRealTimeBidding.data as T,
      loading: reactiveRealTimeBidding.loading,
      error: reactiveRealTimeBidding.error,
    },
  };
};

export default useRealTimeBidding;
